import { useEffect } from 'react';

const BASE_URL   = 'https://utxomacro.com';
const SITE_NAME  = 'UTXOMacro';
const TWITTER    = '@UTXOMacro';
const DEFAULT_OG = `${BASE_URL}/og-default.jpg`;

interface SEOOptions {
  title?: string;          // page title (without site suffix)
  description?: string;
  image?: string;          // absolute URL or path
  url?: string;            // canonical URL
  type?: 'website' | 'article';
  publishedAt?: string;    // ISO8601 — for article structured data
  author?: string;
  keywords?: string;
  category?: string;       // article category (Bitcoin | Macro | AI)
}

// Stop-words to exclude from auto-keyword extraction
const STOP_WORDS = new Set([
  'a','an','the','and','or','but','in','on','at','to','for','of','with',
  'as','is','are','was','were','be','been','has','have','had','do','does',
  'did','will','would','could','should','may','might','shall','can',
  'its','it','this','that','these','those','by','from','up','about',
  'into','than','then','so','if','while','after','before','over','under',
  'per','vs','via','how','why','what','when','where','who',
]);

function extractKeywords(title: string, category: string, baseKeywords: string): string {
  const titleWords = title
    .toLowerCase()
    .replace(/[^a-z0-9\s%-]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 3 && !STOP_WORDS.has(w));

  // Dedupe and take top 8 meaningful words
  const unique = Array.from(new Set(titleWords)).slice(0, 8);

  // Category-specific boosts
  const categoryBoosts: Record<string, string[]> = {
    Bitcoin:  ['Bitcoin', 'BTC', 'on-chain analysis', 'crypto', 'UTXOMacro'],
    Macro:    ['macroeconomics', 'Federal Reserve', 'inflation', 'interest rates', 'UTXOMacro'],
    AI:       ['artificial intelligence', 'AI disruption', 'automation', 'tech', 'UTXOMacro'],
  };
  const boosts = categoryBoosts[category] ?? ['Bitcoin', 'macro', 'UTXOMacro'];

  const all = [...unique, ...boosts, baseKeywords].join(', ');
  return all;
}

export function useSEO(opts: SEOOptions = {}) {
  useEffect(() => {
    const {
      title,
      description = 'Daily Bitcoin on-chain analysis, macroeconomic intelligence, and AI disruption signals. Facts and some opinions.',
      image,
      url,
      type = 'website',
      publishedAt,
      author = 'UTXOMacro',
      keywords,
      category = '',
    } = opts;

    // Auto-generate rich keywords for articles
    const resolvedKeywords = type === 'article' && title
      ? extractKeywords(title, category, keywords ?? 'Bitcoin, macro, UTXOMacro')
      : (keywords ?? 'Bitcoin, macroeconomics, on-chain analysis, AI, UTXOMacro');

    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Bitcoin, Macro & AI Intelligence`;
    const canonicalUrl = url ? (url.startsWith('http') ? url : `${BASE_URL}/${url}`) : BASE_URL;
    const ogImage = image
      ? (image.startsWith('http') ? image : `${BASE_URL}/${image.replace(/^\//, '')}`)
      : DEFAULT_OG;

    // Title
    document.title = fullTitle;

    // Helper: upsert a <meta> tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.match(/\[(.+?)="(.+?)"\]/)?.slice(1) ?? [];
        if (attrName) el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
      el.href = href;
    };

    // Standard meta
    setMeta('meta[name="description"]',  'content', description);
    setMeta('meta[name="author"]',       'content', author);
    setMeta('meta[name="keywords"]', 'content', resolvedKeywords);
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('meta[property="og:title"]',       'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]',         'content', canonicalUrl);
    setMeta('meta[property="og:image"]',       'content', ogImage);
    setMeta('meta[property="og:type"]',        'content', type);
    if (publishedAt) setMeta('meta[property="article:published_time"]', 'content', publishedAt);

    // Twitter / X
    setMeta('meta[name="twitter:title"]',       'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]',       'content', ogImage);
    setMeta('meta[name="twitter:card"]',        'content', 'summary_large_image');
    setMeta('meta[name="twitter:site"]',        'content', TWITTER);

    // Structured data for articles
    if (type === 'article') {
      const existing = document.querySelector('script[data-type="article-ld"]');
      if (existing) existing.remove();
      const breadcrumbExisting = document.querySelector('script[data-type="breadcrumb-ld"]');
      if (breadcrumbExisting) breadcrumbExisting.remove();

      // NewsArticle schema (preferred by Google News over generic Article)
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-type', 'article-ld');
      const now = new Date().toISOString();
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: title ?? fullTitle,
        description,
        image: [ogImage],
        author: {
          '@type': 'Person',
          name: author,
          sameAs: ['https://twitter.com/UTXOMacro'],
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: { '@type': 'ImageObject', url: DEFAULT_OG },
          sameAs: ['https://twitter.com/UTXOMacro'],
        },
        url: canonicalUrl,
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
        ...(publishedAt ? { datePublished: publishedAt } : {}),
        dateModified: publishedAt ?? now,
      });
      document.head.appendChild(ld);

      // BreadcrumbList schema
      const breadcrumb = document.createElement('script');
      breadcrumb.type = 'application/ld+json';
      breadcrumb.setAttribute('data-type', 'breadcrumb-ld');
      breadcrumb.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
          { '@type': 'ListItem', position: 2, name: 'Articles', item: `${BASE_URL}/articles` },
          { '@type': 'ListItem', position: 3, name: title ?? 'Article', item: canonicalUrl },
        ],
      });
      document.head.appendChild(breadcrumb);
    }

    // Cleanup article LD on unmount if not an article page
    return () => {
      if (type !== 'article') {
        document.querySelector('script[data-type="article-ld"]')?.remove();
        document.querySelector('script[data-type="breadcrumb-ld"]')?.remove();
      }
    };
  }, [opts.title, opts.description, opts.image, opts.url, opts.type, opts.publishedAt]);
}
